import { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { motion } from 'framer-motion'
import { HiMail, HiPhone, HiLocationMarker, HiPaperAirplane } from 'react-icons/hi'
import { FaLinkedin, FaGithub } from 'react-icons/fa'
import toast from 'react-hot-toast'
import type { Language } from '../App'
import { t } from '../utils/translations'
import { personalInfo } from '../utils/data'
import './Contact.css'

interface ContactProps { language: Language }

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

export default function Contact({ language }: ContactProps) {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [form, setForm] = useState<FormData>({ name: '', email: '', subject: '', message: '' })
  const [loading, setLoading] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill all required fields.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (res.ok) {
        toast.success(t('contact.success', language))
        setForm({ name: '', email: '', subject: '', message: '' })
      } else {
        throw new Error('Server error')
      }
    } catch {
      toast.error(t('contact.error', language))
    } finally {
      setLoading(false)
    }
  }

  const infoItems = [
    { icon: <HiMail />, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: <HiPhone />, label: 'Phone', value: personalInfo.phone, href: `tel:${personalInfo.phone}` },
    { icon: <HiLocationMarker />, label: 'Location', value: personalInfo.location, href: undefined },
  ]

  const socials = [
    { icon: <FaLinkedin />, href: personalInfo.linkedin, label: 'LinkedIn' },
    { icon: <FaGithub />, href: personalInfo.github, label: 'GitHub' },
  ]

  const inputVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: (i: number) => ({
      x: 0, opacity: 1,
      transition: { duration: 0.5, delay: i * 0.1 }
    })
  }

  return (
    <section id="contact" ref={ref}>
      <div className="orb orb-purple contact-orb-1" />
      <div className="orb orb-pink contact-orb-2" />

      <div className="container">
        <motion.div
          className="section-header"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label">{t('contact.label', language)}</p>
          <h2 className="section-title">{t('contact.title', language)}</h2>
          <div className="gradient-divider" />
          <p className="section-desc">{t('contact.desc', language)}</p>
        </motion.div>

        <div className="contact-layout">
          {/* Info column */}
          <motion.div
            className="contact-info"
            initial={{ x: -40, opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="contact-info-card card">
              <h3 className="contact-info-title">Let's work together</h3>
              <p className="contact-info-desc">
                I'm always open to discussing new projects, creative ideas, or opportunities to be part of amazing teams.
              </p>

              <div className="contact-items">
                {infoItems.map((item, i) => (
                  <motion.div key={i} className="contact-item" whileHover={{ x: 4 }}>
                    <div className="contact-item__icon">{item.icon}</div>
                    <div className="contact-item__text">
                      <span className="contact-item__label">{item.label}</span>
                      {item.href ? (
                        <a href={item.href} className="contact-item__value link">{item.value}</a>
                      ) : (
                        <span className="contact-item__value">{item.value}</span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="contact-socials">
                {socials.map((s, i) => (
                  <motion.a
                    key={i}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social-btn"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={s.label}
                  >
                    {s.icon}
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Status card */}
            <div className="status-card card">
              <div className="status-dot" />
              <div>
                <p className="status-title">Available for opportunities</p>
                <p className="status-sub">Open to freelance & full-time roles</p>
              </div>
            </div>
          </motion.div>

          {/* Form column */}
          <motion.div
            className="contact-form-wrap"
            initial={{ x: 40, opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="contact-form card">
              <div className="form-row">
                <motion.div className="form-group" custom={0} variants={inputVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
                  <label htmlFor="name">{t('contact.name', language)} *</label>
                  <input
                    id="name" name="name" type="text"
                    placeholder="John Doe"
                    value={form.name}
                    onChange={handleChange}
                    className="form-input"
                  />
                </motion.div>

                <motion.div className="form-group" custom={1} variants={inputVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
                  <label htmlFor="email">{t('contact.email', language)} *</label>
                  <input
                    id="email" name="email" type="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    className="form-input"
                  />
                </motion.div>
              </div>

              <motion.div className="form-group" custom={2} variants={inputVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
                <label htmlFor="subject">{t('contact.subject', language)}</label>
                <input
                  id="subject" name="subject" type="text"
                  placeholder="Project Collaboration"
                  value={form.subject}
                  onChange={handleChange}
                  className="form-input"
                />
              </motion.div>

              <motion.div className="form-group" custom={3} variants={inputVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
                <label htmlFor="message">{t('contact.message', language)} *</label>
                <textarea
                  id="message" name="message"
                  placeholder="Tell me about your project..."
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  className="form-input form-textarea"
                />
              </motion.div>

              <motion.button
                className="btn btn-primary submit-btn"
                onClick={handleSubmit}
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02, y: loading ? 0 : -2 }}
                whileTap={{ scale: 0.97 }}
              >
                {loading ? (
                  <span className="spinner" />
                ) : (
                  <HiPaperAirplane style={{ transform: 'rotate(90deg)' }} />
                )}
                {loading ? t('contact.sending', language) : t('contact.send', language)}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
